import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MobileTabBar from "@/components/MobileTabBar";
import CookieConsent from "@/components/CookieConsent";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing"; // Landing loaded eagerly for fast first paint
import ErrorBoundary from "@/components/ErrorBoundary";
import RouteTracker from "@/components/RouteTracker";
import "@/App.css";

// Route-level code splitting (SEO / Lighthouse win: smaller initial bundle)
const Login            = lazy(() => import("@/pages/Login"));
const Register         = lazy(() => import("@/pages/Register"));
const ForgotPassword   = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword    = lazy(() => import("@/pages/ResetPassword"));
const Agencies         = lazy(() => import("@/pages/Agencies"));
const AgencyDetail     = lazy(() => import("@/pages/AgencyDetail"));
const AgencyRegister   = lazy(() => import("@/pages/AgencyRegister"));
const Feed             = lazy(() => import("@/pages/Feed"));
const Forums           = lazy(() => import("@/pages/Forums"));
const ThreadDetail     = lazy(() => import("@/pages/ThreadDetail"));
const Profile          = lazy(() => import("@/pages/Profile"));
const Nurses           = lazy(() => import("@/pages/Nurses"));
const Messages         = lazy(() => import("@/pages/Messages"));
const Subscribe        = lazy(() => import("@/pages/Subscribe"));
const SubscribeSuccess = lazy(() => import("@/pages/SubscribeSuccess"));
const Unsubscribe      = lazy(() => import("@/pages/Unsubscribe"));
const Admin            = lazy(() => import("@/pages/Admin"));
const Trust            = lazy(() => import("@/pages/Trust"));
const Events           = lazy(() => import("@/pages/Events"));
const Polls            = lazy(() => import("@/pages/Polls"));
const News             = lazy(() => import("@/pages/News"));
const Excellence       = lazy(() => import("@/pages/Excellence"));
const Skills           = lazy(() => import("@/pages/Skills"));
const Jobs             = lazy(() => import("@/pages/Jobs"));
const Dashboard        = lazy(() => import("@/pages/Dashboard"));
const Mentors          = lazy(() => import("@/pages/Mentors"));
const Ask              = lazy(() => import("@/pages/Ask"));
const AskDetail        = lazy(() => import("@/pages/AskDetail"));
const AdminAgencies    = lazy(() => import("@/pages/AdminAgencies"));
const AgencyCompare    = lazy(() => import("@/pages/AgencyCompare"));
const Terms            = lazy(() => import("@/pages/Terms"));
const Privacy          = lazy(() => import("@/pages/Privacy"));
const DMCA             = lazy(() => import("@/pages/DMCA"));
const FoundingNurses   = lazy(() => import("@/pages/FoundingNurses"));
const Story            = lazy(() => import("@/pages/Story"));
const Salaries         = lazy(() => import("@/pages/Salaries"));
const AdminAnalytics   = lazy(() => import("@/pages/AdminAnalytics"));
const NotificationSettings = lazy(() => import("@/pages/NotificationSettings"));
const TagFeed          = lazy(() => import("@/pages/TagFeed"));

function RouteFallback() {
  return (
    <div className="p-10 text-center text-black/50 text-sm" role="status" aria-live="polite">
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Navbar />
          <RouteTracker />
          <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/agencies" element={<Agencies />} />
                <Route path="/agencies/new" element={<AgencyRegister />} />
                <Route path="/agencies/:id" element={<AgencyDetail />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/forums" element={<Forums />} />
                <Route path="/forums/:id" element={<ThreadDetail />} />
                <Route path="/tags/:tag" element={<TagFeed />} />
                <Route path="/nurses" element={<Nurses />} />
                <Route path="/nurses/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
                <Route path="/subscribe/success" element={<ProtectedRoute><SubscribeSuccess /></ProtectedRoute>} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/trust" element={<Trust />} />
                <Route path="/news" element={<News />} />
                <Route path="/excellence" element={<Excellence />} />
                <Route path="/skills" element={<Skills />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/mentors" element={<Mentors />} />
                <Route path="/ask" element={<Ask />} />
                <Route path="/ask/:id" element={<AskDetail />} />
                <Route path="/agencies/compare" element={<AgencyCompare />} />
                <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
                <Route path="/polls" element={<ProtectedRoute><Polls /></ProtectedRoute>} />
                <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                <Route path="/admin/agencies" element={<ProtectedRoute><AdminAgencies /></ProtectedRoute>} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/dmca" element={<DMCA />} />
                <Route path="/founding-nurses" element={<FoundingNurses />} />
                <Route path="/story" element={<Story />} />
                <Route path="/salaries" element={<Salaries />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/settings/notifications" element={<NotificationSettings />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
            <Footer />
            <MobileTabBar />
            <CookieConsent />
            <Toaster position="top-center" />
          </AuthProvider>
        </BrowserRouter>
    </div>
  );
}
